import { Route } from 'react-router-dom';
import { apiDocsPlugin } from '@backstage/plugin-api-docs';
import { CatalogEntityPage, CatalogIndexPage, catalogPlugin } from '@backstage/plugin-catalog';
import { catalogImportPlugin } from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { CustomTemplateListPage } from './components/ScaffolderPage';
import { orgPlugin } from '@backstage/plugin-org';
import { TechDocsIndexPage, techdocsPlugin, TechDocsReaderPage } from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';
import { SettingsLayout, UserSettingsPage } from '@backstage/plugin-user-settings';
import apiDocsModule from './modules/apis/apiDocs.module';
import featureFlagsModule from './modules/feature-flags.module';
import { NEW_FRONTEND_SYSTEM_FLAG } from '@internal/frontend-common';
import { featureFlagsApi } from './modules/featureFlags';
import { EntityPage } from './components/catalog/EntityPage';

import { createApp } from '@backstage/frontend-defaults';
import {
  convertLegacyAppOptions,
  convertLegacyAppRoot,
  convertLegacyRouteRef,
  convertLegacyRouteRefs,
} from '@backstage/core-compat-api';
import { FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';

import { UserNotificationSettingsCard } from '@backstage/plugin-notifications';
import { shortcutsPlugin } from '@backstage-community/plugin-shortcuts';
import { EDSCheckboxGroupFieldExtension } from './scaffolder/CheckboxGroup';
import { EntraUserTokenExtension } from './scaffolder/EntraUserToken';
import { AzUserTokenExtension } from './scaffolder/AzUserToken';
import { GitHubUserTokenExtension } from './scaffolder/GitHubUserToken';
import { EntraGroupPickerExtension } from './scaffolder/EntraGroupPicker';
import { AzureSubscriptionPickerExtension } from './scaffolder/AzureSubscriptionPicker';
import { AzureResourceGroupPickerExtension } from './scaffolder/AzureResourceGroupPicker';
import { AppBlueprintPickerExtension } from './scaffolder/AppBlueprintPicker';
import { OwnedEntityPickerExtension } from './scaffolder/OwnedEntityPicker/extensions';
import { ConfigurationItemPickerExtension } from './scaffolder/ConfigurationItemPicker';
import { NoticeBannerExtension } from './scaffolder/NoticeBanner';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import { themesModule } from './themes/themes.module';
import { signInPageModule } from './modules/signIn/signInPage.module';
import { microsoftAzUserAuthModule } from './modules/microsoftAzUserAuth';
import { homePageModule } from './modules/home/homePage.module';
import { CustomTechdocsStyling } from '@internal/techdocs-addon-custom-styling';
import { Katex } from '@internal/techdocs-addon-katex';
import { TechdocsDisclaimerBanner } from '@internal/techdocs-addon-disclaimer-banner';
import { notFoundErrorPageModule } from './modules/CustomErrorNotFoundPage';

import './themes/bui.theme.varia.css';

import { SearchPage } from '@backstage/plugin-search';
import { searchPage } from './modules/search/SearchPage';
import { reactQueryClientWrapperModule } from './modules/react-query.module';
import { CatalogFilters } from './components/catalog/CatalogFilters';
import { catalogColumns } from './components/catalog/catalogColumns';
import { temporaryOmitPageHeaderModule } from './modules/temporaryOmitPageHeaderModule';
import { sidebarNavModule } from './modules/nav/sidebarNav.module.tsx';
import { appIconsModule } from './modules/icons/appIcons.module';
import { uiTestPageModule } from './modules/uiTest/uiTest.module.tsx';
import { applicationInsightsModule } from './modules/applicationInsights/applicationInsights.module';
import { usageAnalyticsModule } from './modules/usageAnalytics/usageAnalytics.module';
import { nfsCatalogPlugin } from './modules/catalog/nfsCatalogPlugin';
import { customPagesModule } from './modules/customPages/customPages.module';
import {
  nfsNotificationsPlugin,
  notificationSettingsModule,
} from './modules/notifications/notifications.module';
import { nfsTechdocsPlugin } from './modules/techdocs/nfsTechdocsPlugin';
import {
  nfsScaffolderPlugin,
  scaffolderFormFieldsModule,
} from './modules/scaffolder/nfsScaffolderPlugin';
import {
  nfsApiDocsPlugin,
  nfsCatalogGraphPlugin,
  nfsCodeInsightsPlugin,
  nfsGithubActionsPlugin,
  nfsGithubPullRequestsPlugin,
} from './modules/catalog/nfsEntityPagePlugins';
import { nfsRadixPlugin } from '@internal/backstage-plugin-radix/alpha';

const newFrontendSystemEnabled = featureFlagsApi.isActive(NEW_FRONTEND_SYSTEM_FLAG);

const convertedOptionsModule = convertLegacyAppOptions({
  plugins: [shortcutsPlugin],
});

// Served natively when the new frontend system is on.
const legacyRoutes = (
  <FlatRoutes>
    <Route
      path="/catalog"
      element={<CatalogIndexPage columns={catalogColumns} filters={<CatalogFilters />} />}
    />
    <Route path="/catalog/:namespace/:kind/:name" element={<CatalogEntityPage />}>
      {EntityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route path="/docs/:namespace/:kind/:name/*" element={<TechDocsReaderPage />}>
      <TechDocsAddons>
        <CustomTechdocsStyling />
        <TechdocsDisclaimerBanner />
        <ReportIssue />
        <Mermaid />
        <Katex />
      </TechDocsAddons>
    </Route>
    <Route
      path="/create"
      element={
        <ScaffolderPage
          components={{
            EXPERIMENTAL_TemplateListPageComponent: CustomTemplateListPage,
          }}
        />
      }
    >
      <ScaffolderFieldExtensions>
        <EDSCheckboxGroupFieldExtension />
        <EntraUserTokenExtension />
        <AzUserTokenExtension />
        <GitHubUserTokenExtension />
        <EntraGroupPickerExtension />
        <AzureSubscriptionPickerExtension />
        <AzureResourceGroupPickerExtension />
        <AppBlueprintPickerExtension />
        <OwnedEntityPickerExtension />
        <ConfigurationItemPickerExtension />
        <NoticeBannerExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route path="/settings" element={<UserSettingsPage />}>
      <SettingsLayout.Route path="/notifications" title="Notifications">
        <UserNotificationSettingsCard
          originNames={{
            'plugin:scaffolder': 'Scaffolder',
          }}
        />
      </SettingsLayout.Route>
    </Route>
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
  </FlatRoutes>
);

const convertedRootFeatures = newFrontendSystemEnabled ? [] : convertLegacyAppRoot(legacyRoutes);

const app = createApp({
  features: [
    // Must come before the converted legacy features so these native plugins own their routes.
    // Their order is also the entity tab order.
    ...(newFrontendSystemEnabled
      ? [
          nfsGithubPullRequestsPlugin,
          nfsGithubActionsPlugin,
          nfsApiDocsPlugin,
          nfsCatalogPlugin,
          nfsTechdocsPlugin,
          nfsCatalogGraphPlugin,
          nfsCodeInsightsPlugin,
          nfsScaffolderPlugin,
          nfsRadixPlugin,
          notificationSettingsModule,
          scaffolderFormFieldsModule,
        ]
      : []),
    nfsNotificationsPlugin,
    convertedOptionsModule,
    apiDocsModule,
    featureFlagsModule,
    signInPageModule,
    microsoftAzUserAuthModule,
    themesModule,
    homePageModule,
    notFoundErrorPageModule,
    reactQueryClientWrapperModule,
    ...(newFrontendSystemEnabled ? [] : [temporaryOmitPageHeaderModule]),
    appIconsModule,
    sidebarNavModule,
    uiTestPageModule,
    customPagesModule,
    applicationInsightsModule,
    usageAnalyticsModule,
    ...convertedRootFeatures,
  ],
  bindRoutes({ bind }) {
    bind(convertLegacyRouteRefs(catalogPlugin.externalRoutes), {
      createComponent: convertLegacyRouteRef(scaffolderPlugin.routes.root),
      viewTechDoc: convertLegacyRouteRef(techdocsPlugin.routes.docRoot),
      createFromTemplate: convertLegacyRouteRef(scaffolderPlugin.routes.selectedTemplate),
    });
    bind(convertLegacyRouteRefs(apiDocsPlugin.externalRoutes), {
      registerApi: convertLegacyRouteRef(catalogImportPlugin.routes.importPage),
    });
    bind(convertLegacyRouteRefs(scaffolderPlugin.externalRoutes), {
      registerComponent: convertLegacyRouteRef(catalogImportPlugin.routes.importPage),
      viewTechDoc: convertLegacyRouteRef(techdocsPlugin.routes.docRoot),
    });
    bind(convertLegacyRouteRefs(orgPlugin.externalRoutes), {
      catalogIndex: convertLegacyRouteRef(catalogPlugin.routes.catalogIndex),
    });
  },
});

export default app.createRoot();
